import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { Code, Apps, Brush, RocketLaunch } from '@mui/icons-material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './servizi.scss';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    icon: <Code />,
    title: 'Web Development',
    description: 'Siti web professionali, veloci e SEO-friendly. Dalla vetrina al portale aziendale completo, realizzato su misura.',
    features: ['Design responsive (mobile, tablet, desktop)', 'Ottimizzazione SEO inclusa', 'Form contatti e integrazioni', 'Velocità e performance top']
  },
  {
    icon: <Apps />,
    title: 'Web App & Gestionali',
    description: 'Sviluppo web app moderne e scalabili con React e TypeScript. Dashboard, gestionali, piattaforme SaaS.',
    features: ['Codice pulito e manutenibile', 'Design UI/UX studiato', 'Performance ottimizzate', 'Scalabilità garantita']
  },
  {
    icon: <Brush />,
    title: 'UI/UX Design',
    description: 'Progetto esperienze utente che funzionano. Prototipi interattivi, test di usabilità, design system completi.',
    features: ['Ricerca utenti e competitor', 'Wireframe e prototipi Figma', 'Test su dispositivi reali', 'Design che converte']
  },
  {
    icon: <RocketLaunch />,
    title: 'Landing Page',
    description: 'Landing page ad alta conversione per campagne marketing e generazione lead. Design persuasivo + copy efficace.',
    features: ['Call-to-action ottimizzate', 'Design orientato alla vendita', 'Layout professionale', 'Caricamento veloce']
  }
];

const technologies = [
  { name: 'React', img: '/tecnologie/react.webp' },
  { name: 'TypeScript', img: '/tecnologie/ts.webp' },
  { name: 'HTML', img: '/tecnologie/html.webp' },
  { name: 'SCSS', img: '/tecnologie/scss.webp' },
  { name: 'GSAP', img: '/tecnologie/gsap.webp' },
  { name: 'Three.js', img: '/tecnologie/threejs.webp' },
  { name: 'Figma', img: '/tecnologie/figma.webp' },
  { name: 'Python', img: '/tecnologie/python.webp' }
];

function Servizi() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Video parallax
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cards animation
    const cards = document.querySelectorAll('.service-card-full, .tech-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Creazione Siti Web Aziendali | Web Designer Freelance Modena</title>
        <meta name="description" content="Realizzo siti web aziendali, landing page e web app in React. Restyling siti obsoleti, UI/UX design, SEO incluso. Lavoro con PMI in Emilia-Romagna e Nord Italia." />
        <meta name="keywords" content="creazione siti web, realizzazione siti web, sito web aziendale, restyling sito web, landing page professionale, UI UX design, web designer Emilia Romagna" />
        <meta property="og:title" content="Creazione Siti Web Aziendali | Web Designer Freelance Modena" />
        <meta property="og:description" content="Realizzo siti web aziendali, landing page e web app in React. Restyling siti obsoleti, UI/UX design, SEO incluso." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/servizi" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/servizi" />
      </Helmet>

      <div className="servizi-page">
        {/* Hero Section */}
        <section className="servizi-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Cosa posso fare per te
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">I miei</span>
                  <span className="hero-line"><span className="gradient-text">Servizi</span></span>
                </h1>

                <p className="hero-description">
                  Dalla strategia al design, dallo sviluppo al lancio. Offro soluzioni digitali
                  complete per far crescere il tuo business online.
                </p>

                <button
                  className="hero-cta"
                  onClick={() => navigateWithTransition('/contatti')}
                >
                  Richiedi preventivo
                  <span className="arrow">→</span>
                </button>
              </div>

              <div className="hero-services-preview">
                <div className="service-preview-card">
                  <span className="preview-icon"><Code /></span>
                  <span className="preview-label">Web Development</span>
                </div>
                <div className="service-preview-card">
                  <span className="preview-icon"><Apps /></span>
                  <span className="preview-label">Web App</span>
                </div>
                <div className="service-preview-card">
                  <span className="preview-icon"><Brush /></span>
                  <span className="preview-label">UI/UX</span>
                </div>
                <div className="service-preview-card">
                  <span className="preview-icon"><RocketLaunch /></span>
                  <span className="preview-label">Landing</span>
                </div>
              </div>
            </div>
          </Container>

          <div className="scroll-indicator">
            <div className="scroll-line"></div>
            <span>Scroll</span>
          </div>
        </section>

        {/* Services Section */}
        <section className="services-detail-section">
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">01</span>
                Servizi
              </h2>
            </div>

            <div className="services-detail-grid">
              {services.map((service, index) => (
                <div key={index} className="service-card-full">
                  <div className="service-header">
                    <div className="service-icon">{service.icon}</div>
                    <h3 className="service-title">{service.title}</h3>
                  </div>
                  <p className="service-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Technologies Section */}
        <section className="tech-section" style={{ backgroundImage: "url('/sfondi/sfondo2.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">02</span>
                Tecnologie
              </h2>
            </div>

            <div className="tech-grid">
              {technologies.map((tech, index) => (
                <div key={index} className="tech-card">
                  <img src={tech.img} alt={tech.name} loading="lazy" />
                  <span className="tech-name">{tech.name}</span>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Servizi;
